import { FlexLayout, H4 } from '@deity/falcon-ui';
import type {
  IEndpointAuthentication,
  IEndpointBasicAuthentication,
  IEndpointBearerTokenAuthentication,
  IEndpointOAuth2Authentication,
  IEndpointTokenAuthentication
} from '@deity-io/di-data-flows';
import { Select } from '@deity-io/portal-ui';
import type { FC } from 'react';
import BasicAuthentication from './Authentication/BasicAuthentication';
import BearerTokenAuthentication from './Authentication/BearerTokenAuthentication';
import Oauth2Authentication from './Authentication/Oauth2Authentication';
import TokenAuthentication from './Authentication/TokenAuthentication';

interface IAuthorizationProps {
  isEditable: boolean;
  authentication?: IEndpointAuthentication;
  parent?: IEndpointAuthentication;
  onChange: (key: string[], value: any | null, set?: boolean) => void;
}

const Authentication: FC<IAuthorizationProps> = ({ isEditable, authentication, parent, onChange }) => {
  const inheritedSetting = !authentication?.type;
  const label = inheritedSetting ? `${parent?.type?.toUpperCase() || 'NONE'} (inherited)` : 'Type';
  const authType = authentication?.type || parent?.type;
  const useParent = authType !== undefined && parent?.type === authType;

  return (
    <FlexLayout p="xxl" flexDirection="column">
      <H4 fontWeight="semiBold" fontSize="xl">
        Authentication & Authorization
      </H4>
      <Select
        label={label}
        sx={{ marginY: 1 }}
        onChange={(val: any) => onChange(['authentication', 'type'], val.value)}
        options={['none', 'token', 'oauth2', 'basic', 'bearer'].map(auth => ({
          value: auth,
          label: auth.toUpperCase()
        }))}
        placeholder="select type"
        value={
          authentication?.type ? { label: authentication?.type.toUpperCase(), value: authentication?.type } : undefined
        }
        disabled={!isEditable}
      />
      {!inheritedSetting && authType === 'token' && (
        <TokenAuthentication
          authentication={authentication as Partial<IEndpointTokenAuthentication>}
          parent={useParent ? (parent as Partial<IEndpointTokenAuthentication>) : undefined}
          isEditable={isEditable}
          onChange={onChange}
        />
      )}
      {!inheritedSetting && authType === 'oauth2' && (
        <Oauth2Authentication
          authentication={authentication as Partial<IEndpointOAuth2Authentication>}
          parent={useParent ? (parent as Partial<IEndpointOAuth2Authentication>) : undefined}
          isEditable={isEditable}
          onChange={onChange}
        />
      )}
      {!inheritedSetting && authType === 'basic' && (
        <BasicAuthentication
          authentication={authentication as Partial<IEndpointBasicAuthentication>}
          parent={useParent ? (parent as Partial<IEndpointBasicAuthentication>) : undefined}
          isEditable={isEditable}
          onChange={onChange}
        />
      )}
      {!inheritedSetting && authType === 'bearer' && (
        <BearerTokenAuthentication
          authentication={authentication as Partial<IEndpointBearerTokenAuthentication>}
          parent={useParent ? (parent as Partial<IEndpointBearerTokenAuthentication>) : undefined}
          isEditable={isEditable}
          onChange={onChange}
        />
      )}
    </FlexLayout>
  );
};

export default Authentication;
